/** @format */
import { ChangeDetectionStrategy, Component, Inject, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FudisDialogService } from '@funidata/ngx-fudis';
import { ValidatablePlan } from 'common-typescript';
import {
    Attainment,
    CourseUnit,
    CourseUnitAttainment,
    CourseUnitSubstitution,
    CreditRange,
    LocalizedString,
    OtmId,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import { combineLatest, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AuthService, UuidService } from 'sis-common';
import { AppErrorHandler, CourseUnitEntityService } from 'sis-components';
import CourseUnitHelper from '../../common/components/course-unit-info-modal/course-unit-info-modal-substitutions/course-unit-info-modal-substitutions-correspondences-select-form/course-unit-helper';
import { SubstitutionSelectOption } from '../../common/components/course-unit-info-modal/course-unit-info-modal-substitutions/course-unit-info-modal-substitutions-correspondences-select-form/course-unit-info-modal-substitutions-correspondences-select-form.component';
import { SubstitutionSelectOptionsService } from '../../common/components/course-unit-info-modal/course-unit-info-modal-substitutions/course-unit-info-modal-substitutions-correspondences-select-form/substitution-select-options.service';
import { AttainmentStudentService } from '../../common/service/attainment-student.service';

export interface SelectSubstitutionDialogData {
    courseUnit: CourseUnit;
    validatablePlan: ValidatablePlan;
}

export type SelectSubstitutionDialogResult =
    | { type: 'select'; substitution: CourseUnitSubstitution[]; substituteCourseUnitsByGroupId: { [key: OtmId]: CourseUnit } }
    | { type: 'remove' };

export function selectSubstitutionDialogOpener(): (
    data: SelectSubstitutionDialogData,
) => MatDialogRef<SelectSubstitutionDialogComponent, SelectSubstitutionDialogResult> {
    const dialogService = inject(FudisDialogService);
    return (data: SelectSubstitutionDialogData) => dialogService.open(SelectSubstitutionDialogComponent, { data });
}

export interface CandidateCourseUnit {
    id: OtmId;
    code: string;
    name: LocalizedString;
    credits: CreditRange;
    /** Set only when the substitution uses part of this course unit's credits, not all of them. */
    usedCredits?: number;
}

export interface CandidateRow {
    /** Null identifies the "no selection" row, always first. */
    localId: string | null;
    /** Null for the "no selection" row. */
    optionNumber: number | null;
    courseUnits: CandidateCourseUnit[];
}

const NO_SELECTION_ROW: CandidateRow = { localId: null, optionNumber: null, courseUnits: [] };

@Component({
    selector: 'app-select-substitution-dialog',
    standalone: false,
    templateUrl: './select-substitution-dialog.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectSubstitutionDialogComponent implements OnInit {
    control = new FormControl<string | null>(null);
    candidates$: Observable<CandidateRow[]>;

    private optionsByLocalId: { [localId: string]: SubstitutionSelectOption } = {};
    private substituteCourseUnitsByGroupId: { [key: OtmId]: CourseUnit } = {};
    private hasSelectableCandidates = false;
    /**
     * True when the course unit's current substitution exists but doesn't appear among the selectable
     * candidates (e.g. its substitute has since been attained).
     */
    private currentSubstitutionUnrepresented = false;

    constructor(
        private readonly dialogService: FudisDialogService,
        private readonly uuidService: UuidService,
        private readonly authService: AuthService,
        private readonly attainmentStudentService: AttainmentStudentService,
        private readonly courseUnitEntityService: CourseUnitEntityService,
        private readonly substitutionSelectOptionsService: SubstitutionSelectOptionsService,
        private readonly appErrorHandler: AppErrorHandler,
        @Inject(MAT_DIALOG_DATA) readonly data: SelectSubstitutionDialogData,
    ) {}

    ngOnInit(): void {
        this.candidates$ = this.getCandidates().pipe(
            this.appErrorHandler.defaultErrorHandlerFd(),
            map(({ options, substituteCourseUnitsByGroupId }) => {
                this.substituteCourseUnitsByGroupId = substituteCourseUnitsByGroupId;
                return (options ?? []).filter(option => option.isSelectable);
            }),
            tap(selectableOptions => {
                this.optionsByLocalId = _.keyBy(selectableOptions, 'localId');
                this.hasSelectableCandidates = selectableOptions.length > 0;
                this.preselectCurrentSubstitution(selectableOptions);
            }),
            map(selectableOptions => [NO_SELECTION_ROW, ...selectableOptions.map((option, i) => this.toCandidateRow(option, i + 1))]),
        );
    }

    // Reuses the course unit info modal's substitution services, but only surfaces selectable options.
    // A current substitution whose substitute has since been attained (or any other non-selectable option) is left off the radio list entirely.
    private getCandidates(): Observable<{
        options: SubstitutionSelectOption[];
        substituteCourseUnitsByGroupId: { [key: OtmId]: CourseUnit };
    }> {
        const courseUnit = this.data.courseUnit;
        const substitutionOptions = (courseUnit.substitutions ?? []).map(substitution => ({
            localId: this.uuidService.randomUUID(),
            substitution,
        }));
        const groupIds = [...new Set(substitutionOptions.flatMap(option => option.substitution).map(s => s.courseUnitGroupId))];

        const allAttainments$: Observable<Attainment[]> = this.authService.loggedIn()
            ? this.attainmentStudentService.getMyValidAttainments()
            : of([]);
        const substituteCourseUnits$: Observable<CourseUnit[]> = groupIds.length
            ? this.courseUnitEntityService.getByGroupIds(groupIds)
            : of([]);

        return combineLatest([substituteCourseUnits$, allAttainments$]).pipe(
            map(([substituteCourseUnits, allAttainments]) => {
                const substituteCourseUnitsByGroupId: { [key: OtmId]: CourseUnit } = _.keyBy(substituteCourseUnits, 'groupId');
                const courseUnitAttainments = allAttainments.filter(
                    attainment => attainment.type === 'CourseUnitAttainment',
                ) as CourseUnitAttainment[];
                const attainmentsByCourseUnitGroupId = _.keyBy(courseUnitAttainments, 'courseUnitGroupId');
                const isEditable = CourseUnitHelper.isEditable(courseUnit, this.data.validatablePlan);
                const options = this.substitutionSelectOptionsService.getSubstitutionSelectOptions(
                    substitutionOptions,
                    attainmentsByCourseUnitGroupId,
                    allAttainments,
                    this.data.validatablePlan,
                    substituteCourseUnitsByGroupId,
                    isEditable,
                    false, // versionChangeInProgress: not applicable when opening this dialog from the plan structure tree
                    true, // showCrossStudySubstitutes
                );
                return { options: options ?? [], substituteCourseUnitsByGroupId };
            }),
        );
    }

    private preselectCurrentSubstitution(selectableOptions: SubstitutionSelectOption[]): void {
        this.currentSubstitutionUnrepresented = false;
        const currentSubstitutedBy = this.data.validatablePlan.getSubstitutedBy(this.data.courseUnit);
        const currentSubstitution = this.data.validatablePlan.findFirstMatchingSubstitution(
            this.data.courseUnit.substitutions,
            currentSubstitutedBy,
        );
        if (_.isEmpty(currentSubstitution)) {
            return;
        }
        const currentGroupIds = currentSubstitution.map(s => s.courseUnitGroupId);
        const selected = selectableOptions.find(option =>
            _.isEmpty(
                _.xor(
                    option.substitution.map(s => s.courseUnitGroupId),
                    currentGroupIds,
                ),
            ),
        );
        if (selected) {
            this.control.setValue(selected.localId);
        } else {
            this.currentSubstitutionUnrepresented = true;
        }
    }

    private toCandidateRow(option: SubstitutionSelectOption, optionNumber: number): CandidateRow {
        return {
            localId: option.localId,
            optionNumber,
            courseUnits: option.substitution.map(courseUnitSubstitution => {
                const courseUnit = this.substituteCourseUnitsByGroupId[courseUnitSubstitution.courseUnitGroupId];
                return {
                    id: courseUnit?.id,
                    code: courseUnit?.code,
                    name: courseUnit?.name,
                    credits: courseUnit?.credits,
                    usedCredits: courseUnitSubstitution.credits,
                };
            }),
        };
    }

    courseUnitInfoUrl(courseUnitId: OtmId): string {
        return `/student/courseunit/${courseUnitId}`;
    }

    // Description element ids only. Excludes "view details" links so they stay out of the radio's description.
    optionAriaDescribedBy(row: CandidateRow): string {
        return row.courseUnits.map((_courseUnit, optionNumber) => this.courseUnitDescriptionId(row, optionNumber)).join(' ');
    }

    courseUnitDescriptionId(row: CandidateRow, index: number): string {
        return `substitution-option-cu-desc-${row.localId}-${index}`;
    }

    save(): void {
        if (!this.hasSelectableCandidates) {
            return;
        }
        if (this.control.value === null) {
            if (this.currentSubstitutionUnrepresented && this.control.pristine) {
                return;
            }
            this.dialogService.close<SelectSubstitutionDialogResult>({ type: 'remove' });
            return;
        }
        const option = this.optionsByLocalId[this.control.value];
        this.dialogService.close<SelectSubstitutionDialogResult>({
            type: 'select',
            substitution: option.substitution,
            substituteCourseUnitsByGroupId: this.substituteCourseUnitsByGroupId,
        });
    }
}
